import { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Paper, Divider, Chip } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { EntityDetailContext, EntityDetailRenderer, EntityDetailTab } from '../types';
import { useCatalogImageMetadata, useEntity } from '@/hooks';
import { catalogImageService } from '@/services/catalogImageService';
import { JsonFormsRenderer } from '@/components/JsonFormsRenderer';
import { useCatalogLocationLabels } from '@/hooks/useCatalogLocationLabels';
/**
 * Catalog-specific entity detail renderer.
 * Provides custom layout for catalog items with image display.
 */
export const createCatalogDetailRenderer = (): EntityDetailRenderer => {
  return {
    renderTabs: (context: EntityDetailContext): EntityDetailTab[] => {
      const { entity, entityConfig } = context;

      const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) {
          return 'N/A';
        }
        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2);
        }
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        return String(value);
      };

      const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      };

      // Create a component for the catalog details tab (needs hooks)
      const CatalogDetailsTab = () => {
        const { data: imageMetadata } = useCatalogImageMetadata(entity.id);
        const [catalogImageUrl, setCatalogImageUrl] = useState<string | null>(null);
        const { primaryLocationLabel, secondaryLocationLabel } = useCatalogLocationLabels();

        // Detect sized variant and fetch parent for expiration inheritance
        const parentId = entity.entity_data?.parent_id as string | undefined;
        const isVariant = typeof parentId === 'string' && parentId.trim().length > 0;
        const { data: parentEntity } = useEntity(parentId ?? '', 'catalog');

        // Determine if expiration fields are inherited from parent
        const childExpStart = entity.entity_data?.expiration_start_date;
        const childExpDuration = entity.entity_data?.expiration_duration_from_start_date;
        const childHasExpiration = Boolean(childExpStart) || Boolean(childExpDuration);

        const parentExpStart = parentEntity?.entity_data?.expiration_start_date;
        const parentExpDuration = parentEntity?.entity_data?.expiration_duration_from_start_date;
        const parentHasExpiration = Boolean(parentExpStart) || Boolean(parentExpDuration);

        const inheritsExpiration = isVariant && !childHasExpiration && parentHasExpiration;

        // Merge inherited expiration fields into display data
        const displayData = useMemo(() => {
          const base = {
            ...entity.entity_data,
            entity_identifier: entity.entity_identifier,
            status: entity.status,
          };
          if (inheritsExpiration && parentEntity) {
            return {
              ...base,
              expiration_start_date: parentEntity.entity_data?.expiration_start_date,
              expiration_duration_from_start_date: parentEntity.entity_data?.expiration_duration_from_start_date,
            };
          }
          return base;
        }, [entity, inheritsExpiration, parentEntity]);

        const locationLabelOverrides = useMemo(() => ({
          '#/properties/primary_location_id': primaryLocationLabel,
          '#/properties/secondary_location_id': secondaryLocationLabel,
        }), [primaryLocationLabel, secondaryLocationLabel]);

        useEffect(() => {
          let objectUrl: string | null = null;
          let cancelled = false;

          if (entity?.id && imageMetadata?.hasImage) {
            (async () => {
              try {
                const url = await catalogImageService.getImageObjectUrl(entity.id);
                if (!cancelled) {
                  objectUrl = url;
                  setCatalogImageUrl(url);
                }
              } catch {
                if (!cancelled) {
                  setCatalogImageUrl(null);
                }
              }
            })();
          } else {
            setCatalogImageUrl(null);
          }

          return () => {
            cancelled = true;
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
            }
          };
        }, [imageMetadata?.hasImage, imageMetadata?.fileSize, imageMetadata?.fileName]);

        return (
          <Box>
            {/* Catalog Image Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <Box sx={{ flex: '0 0 auto' }}>
                    {imageMetadata?.hasImage && catalogImageUrl ? (
                      <Box
                        component="img"
                        src={catalogImageUrl}
                        alt={imageMetadata.fileName ?? 'Catalog image'}
                        sx={{
                          maxWidth: 240,
                          maxHeight: 240,
                          borderRadius: 1,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 240,
                          height: 240,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          border: (theme) => `1px dashed ${theme.palette.divider}`,
                          color: 'text.secondary',
                          fontSize: 14,
                        }}
                      >
                        No image uploaded
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flex: '1 1 auto' }}>
                    <Typography variant="h6" gutterBottom>
                      Catalog Image
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Thumbnail image for this catalog item. To update the image, use the Edit Catalog
                      modal.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Entity Data Section */}
            <Card>
              <CardContent>
                {inheritsExpiration && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Expiration settings inherited from parent"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                )}
                {entityConfig?.entity_schema ? (
                  <JsonFormsRenderer
                    schema={entityConfig.entity_schema}
                    uiSchema={entityConfig.ui_schema}
                    data={displayData}
                    readonly={true}
                    labelOverrides={locationLabelOverrides}
                  />
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.100' }}>
                    <pre
                      style={{
                        margin: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {formatValue(entity.entity_data)}
                    </pre>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Box>
        );
      };

      return [
        {
          label: 'Catalog Details',
          content: <CatalogDetailsTab />,
        },
        {
          label: 'System Information',
          content: (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      eQuip System Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Entity ID:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {entity.id}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{entity.status}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Version:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{entity.version}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Timestamps */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      eQuip Asset Management System
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created in eQuip:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{formatDate(entity.created_at)}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Updated in eQuip:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{formatDate(entity.updated_at)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ),
        },
      ];
    },
  };
};
